import { useEffect, useRef, useState } from 'react';

/**
 * Scratch-to-reveal overlay — the morning ritual gesture.
 *
 * Behaviour:
 *  • Black-gold layer covers the children with a centred prompt.
 *  • As user drags, we erase pixels under the finger.
 *  • Once >= `revealThreshold` (default 35%) is erased, we call `onReveal()`
 *    and fade the remaining layer out.
 *
 * Notes:
 *  • Canvas uses `willReadFrequently:true` — we sample pixels often.
 *  • Label is auto-wrapped to fit inside the card (no clipping for long
 *    Russian phrases like "Сотри, чтобы открыть").
 *  • HapticFeedback is only fired on Telegram WebApp v6.1+ (older clients
 *    log noisy warnings).
 */
interface Props {
  width?: number;
  height?: number;
  revealThreshold?: number;     // 0–1
  brushRadius?: number;
  label?: string;
  /** Optional URL of an image to use as the scratch surface. When set,
   *  the image fills the canvas (instead of the dark gradient) and the
   *  label is rendered as a small bottom-of-card hint rather than a
   *  centred prompt. Used for the daily card so the surface is the
   *  deck's actual back illustration. */
  backgroundImage?: string;
  onReveal?: () => void;
  children: React.ReactNode;
}

export default function ScratchCard({
  width = 280,
  height = 420,
  revealThreshold = 0.35,
  brushRadius = 26,
  label = '✦',
  backgroundImage,
  onReveal,
  children,
}: Props) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [revealed, setRevealed] = useState(false);
  const isDrawing = useRef(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    if (!ctx) return;

    /**
     * Centred-prompt path — original behaviour used when no backgroundImage
     * is provided. Auto-wraps the label to fit, picks a comfortable
     * font size, draws a star above. Kept for screens that don't have
     * an art asset.
     */
    const paintGradientWithCentredPrompt = () => {
      // 1) Background gradient
      const gradient = ctx.createLinearGradient(0, 0, 0, height);
      gradient.addColorStop(0, '#2a2156');
      gradient.addColorStop(1, '#0b0820');
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, width, height);

      // 2) Centred prompt — auto-wrap to fit inside the card.
      ctx.fillStyle = '#e8c356';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';

      const fits = (size: number) => {
        ctx.font = `${size}px Georgia, serif`;
        const words = label.split(' ');
        return words.every((w) => ctx.measureText(w).width <= width * 0.8);
      };
      let fontSize = Math.min(34, Math.floor(width * 0.13));
      while (fontSize > 14 && !fits(fontSize)) fontSize -= 2;
      ctx.font = `${fontSize}px Georgia, serif`;

      const wrap = (text: string, maxWidth: number): string[] => {
        const words = text.split(' ');
        const lines: string[] = [];
        let current = '';
        for (const w of words) {
          const test = current ? `${current} ${w}` : w;
          if (ctx.measureText(test).width <= maxWidth) {
            current = test;
          } else {
            if (current) lines.push(current);
            current = w;
          }
        }
        if (current) lines.push(current);
        return lines;
      };

      const lines = wrap(label, width * 0.8);
      const lineHeight = fontSize * 1.15;
      const startY = height / 2 - ((lines.length - 1) * lineHeight) / 2;
      lines.forEach((ln, i) => {
        ctx.fillText(ln, width / 2, startY + i * lineHeight);
      });

      ctx.font = `${fontSize * 0.9}px Georgia, serif`;
      ctx.fillText('✦', width / 2, startY - lineHeight - 4);
    };

    /**
     * Image-surface path — used when backgroundImage is provided. We
     * fill the canvas with the artwork, then render a SMALL bottom-of-
     * card label so the user still gets the scratch-to-reveal hint
     * without obscuring the art. Image draw is async; the canvas
     * stays blank for a few frames until the image loads, which is
     * acceptable because the parent OrnateFrame still renders.
     */
    const paintImageBackground = (url: string) => {
      const img = new Image();
      img.decoding = 'async';
      img.onload = () => {
        ctx.drawImage(img, 0, 0, width, height);
        // Small bottom hint with a darkened pill behind it so the
        // text reads against any part of the art.
        const hintFontSize = Math.min(15, Math.floor(width * 0.06));
        const padding = 8;
        const text = label;
        ctx.font = `italic ${hintFontSize}px Georgia, serif`;
        const textW = ctx.measureText(text).width;
        const pillW = textW + padding * 2;
        const pillH = hintFontSize + padding;
        const pillX = (width - pillW) / 2;
        const pillY = height - pillH - 14;
        ctx.fillStyle = 'rgba(11, 8, 32, 0.7)';
        const r = pillH / 2;
        ctx.beginPath();
        ctx.moveTo(pillX + r, pillY);
        ctx.lineTo(pillX + pillW - r, pillY);
        ctx.quadraticCurveTo(pillX + pillW, pillY, pillX + pillW, pillY + r);
        ctx.lineTo(pillX + pillW, pillY + pillH - r);
        ctx.quadraticCurveTo(
          pillX + pillW, pillY + pillH,
          pillX + pillW - r, pillY + pillH,
        );
        ctx.lineTo(pillX + r, pillY + pillH);
        ctx.quadraticCurveTo(pillX, pillY + pillH, pillX, pillY + pillH - r);
        ctx.lineTo(pillX, pillY + r);
        ctx.quadraticCurveTo(pillX, pillY, pillX + r, pillY);
        ctx.closePath();
        ctx.fill();
        ctx.fillStyle = '#e8c356';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(text, width / 2, pillY + pillH / 2);
      };
      img.onerror = () => {
        // Fall back to the gradient surface if the image can't load
        // — preserves the scratch experience even when the asset
        // server hiccups.
        paintGradientWithCentredPrompt();
      };
      img.src = url;
    };

    if (backgroundImage) {
      paintImageBackground(backgroundImage);
    } else {
      paintGradientWithCentredPrompt();
    }
  }, [width, height, label, backgroundImage]);

  const haptic = () => {
    const tg = window.Telegram?.WebApp as any;
    if (!tg?.HapticFeedback) return;
    // Telegram added HapticFeedback in WebApp v6.1. Older Telegram clients
    // log loud warnings if we call it. Detect by version string.
    const ver = tg.version ?? '6.0';
    const [maj, min] = ver.split('.').map(Number);
    if (maj > 6 || (maj === 6 && min >= 1)) {
      try { tg.HapticFeedback.notificationOccurred('success'); } catch {}
    }
  };

  const erase = (x: number, y: number) => {
    const canvas = canvasRef.current;
    if (!canvas || revealed) return;
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    if (!ctx) return;
    ctx.globalCompositeOperation = 'destination-out';
    ctx.beginPath();
    ctx.arc(x, y, brushRadius, 0, Math.PI * 2);
    ctx.fill();
    maybeCheckReveal();
  };

  const maybeCheckReveal = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    if (!ctx) return;
    const { data } = ctx.getImageData(0, 0, width, height);
    let transparent = 0;
    const total = (width * height) / 16;
    for (let i = 3; i < data.length; i += 16 * 4) {
      if (data[i] === 0) transparent++;
    }
    if (transparent / total >= revealThreshold) {
      setRevealed(true);
      haptic();
      onReveal?.();
    }
  };

  const pointer = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    erase(e.clientX - rect.left, e.clientY - rect.top);
  };

  return (
    <div className="relative inline-block" style={{ width, height }}>
      <div className="absolute inset-0">{children}</div>
      {!revealed && (
        <canvas
          ref={canvasRef}
          width={width}
          height={height}
          className="absolute inset-0 cursor-grab touch-none transition-opacity duration-700"
          style={{ opacity: revealed ? 0 : 1 }}
          onPointerDown={(e) => {
            (e.currentTarget as HTMLCanvasElement).setPointerCapture(e.pointerId);
            isDrawing.current = true;
            pointer(e);
          }}
          onPointerMove={(e) => {
            if (isDrawing.current) pointer(e);
          }}
          onPointerUp={() => {
            isDrawing.current = false;
          }}
        />
      )}
    </div>
  );
}
